import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async"; 
import {
  ShieldCheck,
  Scale,
  FileText,
  AlertCircle,
  ExternalLink,
  Mail,
  Gavel,
  UserX,
  Copyright
} from "lucide-react";
import Aos from "aos";
import "aos/dist/aos.css";
import { useSEO } from "../Pages/Seo";

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    Aos.init({
      duration: 500,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  useSEO({
    title: "Terms & Conditions | Game Zone Hub",
    description: "Read the Terms and Conditions for Game Zone Hub. Understand our policies regarding free online games, user conduct, intellectual property, and third-party links.",
    keywords: "terms and conditions, game zone hub terms, free online games policy, browser games terms, legal terms",
    canonical: "https://www.gamezonehub.in/terms",
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms & Conditions - Game Zone Hub",
    "description": "Terms of Service and user agreement for playing free online games on Game Zone Hub.",
    "url": "https://www.gamezonehub.in/terms",
    "publisher": {
      "@type": "Organization",
      "name": "Game Zone Hub"
    }
  };

  // 🚀 MASSIVELY EXPANDED CONTENT FOR ADSENSE APPROVAL (LEGAL & DETAILED)
  const sections = [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      icon: <FileText className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Welcome to <strong>Game Zone Hub</strong>. By accessing, browsing, and using our website (the "Website" or "Service"), you acknowledge that you have completely read, understood, and agree to be bound by these Terms and Conditions. These terms apply to all visitors, users, and others who wish to access or <strong>play free online browser games</strong> on our platform.
          </p>
          <p>
            If you disagree with any part of these terms, then you do not have permission to access the Website. You must be at least 13 years of age to use this Website independently. If you are under 13, you may only use this Website under the supervision of a parent or legal guardian who agrees to be bound by these Terms. By using the Website, you represent and warrant that you meet all of the foregoing eligibility requirements.
          </p>
        </div>
      ),
    },
    {
      id: "intellectual",
      title: "2. Intellectual Property & Copyright",
      icon: <ShieldCheck className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            <strong>Game Zone Hub</strong> acts as a hosting portal and publisher for third-party HTML5 <strong>online games</strong>. All individual games, characters, graphical assets, audio files, and gameplay mechanics belong entirely to their respective independent developers, studios, and publishers. These games are provided to us via legitimate game distribution networks (such as GameDistribution) under valid publisher agreements.
          </p>
          <p>
            However, the Website's original logo, UI/UX design, structural layout, underlying code, customized text content, and overall brand identity are the exclusive intellectual property of <strong>Game Zone Hub</strong> and are protected by international copyright laws. 
          </p>
          <p>
            You are granted a limited, non-exclusive, non-transferable, and revocable license to use the Website strictly for your personal, non-commercial entertainment. You may not copy, reproduce, scrape, reverse-engineer, or distribute our original branding, platform architecture, or any hosted games without prior written permission.
          </p>
        </div>
      ),
    },
    {
      id: "user-conduct",
      title: "3. Acceptable User Conduct",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We strive to maintain a safe, fair, and fun environment for all players. Users agree to use the website only for lawful purposes. While enjoying our <strong>free browser games</strong>, you are strictly prohibited from engaging in any of the following activities:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-400 marker:text-cyan-500">
             <li>Using automated bots, web scrapers, macros, hacks, or cheat software to manipulate high scores, game mechanics, or leaderboard rankings.</li>
             <li>Attempting to bypass, compromise, or disrupt the Website's security, servers, or networks (e.g., executing DDoS attacks, SQL injection, or cross-site scripting).</li>
             <li>Using aggressive ad-blockers that intentionally block the scripts required to run the games or interfere with the site's primary revenue generation mechanics, which allow us to keep the games 100% free.</li>
             <li>Posting, linking, or transmitting any malicious software, viruses, trojans, or harmful content.</li>
             <li>Engaging in any activity that restricts or inhibits any other user from using or enjoying the Website.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "third-party",
      title: "4. Third-Party Ads & External Links",
      icon: <ExternalLink className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            To keep our massive library of games completely free for everyone, our Website uses <strong>Google AdSense</strong> and other verified third-party advertising partners to serve advertisements. These third parties may use cookies and web beacons to serve ads based on your prior visits to our site and other sites on the internet. 
          </p>
          <p>
            Our Service may contain links to third-party web sites or services that are not owned or controlled by <strong>Game Zone Hub</strong>. We have no control over, and assume no responsibility for the content, privacy policies, terms of use, or practices of any third-party websites or services. 
          </p>
          <p>
            You acknowledge and agree that Game Zone Hub shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods, or services available on or through any such external web sites or services. Clicking on external links or advertisements is entirely at your own risk.
          </p>
        </div>
      ),
    },
    {
      id: "termination",
      title: "5. Account Termination & Access",
      icon: <UserX className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We may terminate or suspend your access to the Website immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of these Terms and Conditions.
          </p>
          <p>
            All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability. We reserve the right to block specific IP addresses that are found to be spamming our servers or violating our acceptable use policy.
          </p>
        </div>
      ),
    },
    {
      id: "disclaimer",
      title: "6. Disclaimer of Warranties",
      icon: <AlertCircle className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p className="uppercase text-sm tracking-wider font-bold text-white bg-red-500/20 p-3 rounded-lg border border-red-500/30">
            The Website and all games are provided on an "AS IS" and "AS AVAILABLE" basis.
          </p>
          <p>
            <strong>Game Zone Hub</strong> makes no warranties, expressed or implied, that the service to <strong>play free online games</strong> will be continuous, uninterrupted, secure, error-free, or entirely free of bugs or viruses. We do not warrant that the results obtained from the use of the Service will be perfectly accurate or reliable.
          </p>
          <p>
            In no event shall Game Zone Hub, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Website; (ii) any conduct or content of any third party on the Website; (iii) any content obtained from the Website; and (iv) unauthorized access, use, or alteration of your transmissions or content.
          </p>
        </div>
      ),
    },
    {
      id: "governing-law",
      title: "7. Governing Law & Modifications",
      icon: <Gavel className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            These Terms shall be governed and construed in accordance with the laws of the jurisdiction in which Game Zone Hub operates, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
          </p>
          <p>
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time without prior direct notice. By continuing to access or use our Website after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.
          </p>
        </div>
      ),
    },
    {
      id: "contact", 
      title: "8. Contact Information", 
      icon: <Mail className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            If you have any questions, concerns, or legal inquiries regarding these Terms and Conditions, or if you wish to report a violation of these terms, please contact our legal and support team immediately.
          </p>
          <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10 inline-block w-full sm:w-auto">
            <p className="flex flex-wrap items-center gap-2">
              <strong className="text-white">Legal Inquiries:</strong> 
              <a href="mailto:support@gamezonehub.in" className="text-blue-400 font-semibold cursor-pointer hover:text-blue-300 transition-colors">
                 support@gamezonehub.in
              </a>
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
      <div className="relative bg-[#0b1120] min-h-screen text-gray-300 sm:pt-[120px] pt-[100px] pb-11">
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      
      <div className="container mx-auto px-4 relative z-10">
        
        <div className="text-center md:mb-20 mb-11">
          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold mb-3 text-white" data-aos="fade-down">
            Terms &{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
              Conditions
            </span>
          </h1>

          <p className="text-gray-400 max-w-2xl mx-auto" data-aos="fade-up">
            Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
          <p className="text-gray-500 text-sm mt-4 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="100">
             Please read these terms and conditions carefully before using our gaming portal. These terms govern your use of the website and outline your legal rights and responsibilities.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">
          
          <div className="lg:w-1/3">
            <div
              className="sticky top-28 bg-[#111827]/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-lg"
              data-aos="fade-right"
            >
              <h3 className="text-white font-bold text-xl mb-4 border-b border-white/10 pb-3">
                Table of Contents
              </h3>

              <nav className="space-y-1">
                {sections.map((section, i) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                  >
                    <span className="text-blue-500 group-hover:scale-110 transition-transform">
                      {section.icon}
                    </span>
                    <span className="text-sm font-medium">
                      {section.title.split(". ")[1]}
                    </span>
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:w-2/3 space-y-8">
            {sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                data-aos="fade-up"
                className="bg-[#111827] rounded-2xl p-6 sm:p-8 border border-white/5 scroll-mt-32 transition-all duration-300 hover:border-blue-500/30 hover:shadow-xl"
              >
                <div className="flex items-center gap-3 mb-5 border-b border-white/5 pb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400 shadow-inner">
                    {section.icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    {section.title}
                  </h2>
                </div>

                <div className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

    </div>
  );
};

export default Terms;